import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Stakeholder interview-notes surface (T-0076 / E07-F06).
 *
 * Engagement-scoped, mounted under
 * `/api/v1/engagements/:engagement_id/health/interviews`. T-0105 reads
 * the list for the interviews tab and the detail (with AI-extracted
 * proposed findings) for the per-note panel. Creation is wired through
 * the generic create modal (`data-create="interview-note"`, T-0117);
 * trigger-processing lives behind a per-note action.
 *
 * Response shapes match `services/interview-notes.ts`:
 *   - InterviewNoteDto                — list/detail header fields
 *   - GetInterviewNoteResult          — detail + proposed_findings
 *   - ProposedFindingDto + ProposedFindingCitationDto — AI-extracted
 *                                       finding proposals with citations
 */

export const SourceKindSchema = z.enum(['typed_note', 'uploaded_transcript']);
export type SourceKind = z.infer<typeof SourceKindSchema>;

export const ProcessStatusSchema = z.enum([
  'draft',
  'processing',
  'indexed',
  'extracted',
  'failed',
]);
export type ProcessStatus = z.infer<typeof ProcessStatusSchema>;

export const InterviewNoteSchema = z.object({
  id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  title: z.string(),
  body_markdown: z.string().nullable(),
  document_id: z.string().uuid().nullable(),
  source_kind: SourceKindSchema,
  interviewee_label: z.string().nullable(),
  interview_date: z.string().nullable(),
  process_status: ProcessStatusSchema,
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type InterviewNote = z.infer<typeof InterviewNoteSchema>;

export const ProposedFindingCitationSchema = z.object({
  id: z.string().uuid(),
  document_id: z.string().uuid(),
  document_title: z.string(),
  page_number: z.number().int().nullable(),
  section_path: z.string().nullable(),
  span: z.unknown(),
});
export type ProposedFindingCitation = z.infer<typeof ProposedFindingCitationSchema>;

export const ProposedFindingSchema = z.object({
  proposal_id: z.string().uuid(),
  proposal_type: z.string(),
  confidence: z.number(),
  status: z.string(),
  proposed_value: z.unknown(),
  citations: z.array(ProposedFindingCitationSchema),
});
export type ProposedFinding = z.infer<typeof ProposedFindingSchema>;

export const InterviewNoteDetailSchema = InterviewNoteSchema.extend({
  proposed_findings: z.array(ProposedFindingSchema),
});
export type InterviewNoteDetail = z.infer<typeof InterviewNoteDetailSchema>;

/**
 * T-0244: a list row carries what the note card draws, so the tab reads one
 * request: who added the note and its finding proposals counted by the API
 * over the same set the detail lists (confirmed = accepted or accepted
 * with edits; pending = waiting for a person).
 */
export const InterviewNoteListItemSchema = InterviewNoteSchema.extend({
  created_by_name: z.string().nullable().optional(),
  proposed_count: z.number().int().optional(),
  confirmed_count: z.number().int().optional(),
  pending_count: z.number().int().optional(),
});
export type InterviewNoteListItem = z.infer<typeof InterviewNoteListItemSchema>;

/** T-0244: the engagement's notes and their proposals, summed by the API. */
export const InterviewNoteTotalsSchema = z.object({
  notes: z.number().int(),
  proposed: z.number().int(),
  confirmed: z.number().int(),
  pending: z.number().int(),
});
export type InterviewNoteTotals = z.infer<typeof InterviewNoteTotalsSchema>;

export const InterviewNoteListSchema = z.object({
  items: z.array(InterviewNoteListItemSchema),
  next_cursor: z.string().nullable(),
  totals: InterviewNoteTotalsSchema.optional(),
});
export type InterviewNoteList = z.infer<typeof InterviewNoteListSchema>;

export interface ListInterviewNotesOpts {
  process_status?: string;
  cursor?: string;
  limit?: number;
}

export function listInterviewNotes(
  engagementId: string,
  opts: ListInterviewNotesOpts = {},
): Promise<InterviewNoteList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/health/interviews`,
    InterviewNoteListSchema,
    { query: opts as Record<string, string | number | boolean | undefined> },
  );
}

export function getInterviewNote(
  engagementId: string,
  interviewNoteId: string,
): Promise<InterviewNoteDetail> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/health/interviews/${encodeURIComponent(
      interviewNoteId,
    )}`,
    InterviewNoteDetailSchema,
  );
}

export function useInterviewNoteListQuery(
  engagementId: string | undefined,
  opts: ListInterviewNotesOpts = {},
) {
  return useQuery({
    queryKey: ['interviews', engagementId, opts] as const,
    queryFn: () => listInterviewNotes(engagementId!, opts),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

export function useInterviewNoteDetailQuery(
  engagementId: string | undefined,
  interviewNoteId: string | undefined,
) {
  return useQuery({
    queryKey: ['interview-detail', engagementId, interviewNoteId] as const,
    queryFn: () => getInterviewNote(engagementId!, interviewNoteId!),
    enabled: Boolean(engagementId) && Boolean(interviewNoteId),
  });
}

/**
 * Human label for the `source_kind` enum. Mirrors the prototype's chip
 * copy ("typed_note" → "Typed note", "uploaded_transcript" → "Transcript").
 */
export function sourceKindLabel(kind: SourceKind): string {
  switch (kind) {
    case 'typed_note':
      return 'Typed note';
    case 'uploaded_transcript':
      return 'Transcript';
  }
}

/**
 * Human label for the `process_status` enum.
 */
export function processStatusLabel(status: ProcessStatus): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'processing':
      return 'Processing';
    case 'indexed':
      return 'Indexed';
    case 'extracted':
      return 'Extracted';
    case 'failed':
      return 'Failed';
  }
}

// ===========================================================================
// Interview-note writes (T-0071 backends — client wiring added in Phase 1;
// the process_status enum above was also corrected to the server vocabulary
// draft|processing|indexed|extracted|failed — the old client enum made the
// list query throw on any real row).
// ===========================================================================

export interface CreateInterviewNoteBody {
  title: string;
  body_markdown?: string | null;
  interviewee_label?: string | null;
  interview_date?: string | null;
}

const InterviewWriteResultSchema = z
  .object({ id: z.string().optional(), process_status: z.string().optional() })
  .passthrough();

export function useCreateInterviewNoteMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInterviewNoteBody) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/interviews`,
        InterviewWriteResultSchema,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['interviews', engagementId] });
    },
  });
}

export function useProcessInterviewNoteMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ interviewNoteId }: { interviewNoteId: string }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/interviews/${encodeURIComponent(interviewNoteId)}/process`,
        InterviewWriteResultSchema,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['interviews', engagementId] });
    },
  });
}
